import cogoToast from 'cogo-toast';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { wgApi } from '../../../app/src/features/api/wgApi';
import { queryClient } from '../../../app/src/providers/queryClient';
import { AdvisoryReport } from '../types/AdvisoryReport';
import {
  getAdvisoryReportById,
  getUseAdvisoryReportQKey,
} from './getAdvisoryReport';

interface Params {
  reportId: string;
  reportRevisionId?: string;
  conversationId?: string;
  recommendationId?: string;
  lang?: string;
}

const createAdvisoryReportRevision = async ({
  reportId,
  reportRevisionId,
  conversationId,
  recommendationId,
  lang,
}: Params) => {
  const { data, isError } = await wgApi.post<AdvisoryReport>(
    `v2/api/advisory-reports/${reportId}/revisions`,
    {
      report_revision_id: reportRevisionId,
      conversation_id: conversationId,
      recommendation_id: recommendationId,
    },
    { query: { lang } },
  );
  if (isError) throw new Error();
  return data;
};

interface MFnParams {
  reportId: string;
  isEmptyReport?: boolean;
  conversationId?: string;
  recommendationId?: string;
  lang?: string;
}

const createAdvisoryReportRevisionMFn = async ({
  reportId,
  isEmptyReport,
  conversationId,
  recommendationId,
  lang,
}: MFnParams) => {
  let advisoryReport = queryClient.getQueryData<AdvisoryReport>(
    getUseAdvisoryReportQKey(reportId),
  );

  if (!advisoryReport) {
    advisoryReport = await getAdvisoryReportById(reportId);
  }

  const report = await createAdvisoryReportRevision({
    reportId: advisoryReport.report_id,
    lang: lang,
    ...(!isEmptyReport && {
      reportRevisionId: reportId,
      conversationId,
      recommendationId,
    }),
  });

  return report;
};

export const useCreateAdvisoryReportRevision = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation(createAdvisoryReportRevisionMFn, {
    onError: () => {
      cogoToast.error(t('ADVISORY-REPORTS_DETAIL_REVISION-CREATION-FAILED'));
    },
    onSuccess: (data, { reportId: oldReportId }) => {
      queryClient.setQueryData(getUseAdvisoryReportQKey(data.id), data);
      queryClient.invalidateQueries(getUseAdvisoryReportQKey(oldReportId));
    },
  });
};
